import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
  Row,
} from "reactstrap";
import DeleteModal from "../Common/DeleteModal";
import TableContainer from "../Common/TableContainer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getClickPaymentsQuery } from "../../queries";
import { addClickPayments, deleteClickPayments } from "../../api/user";
import { useTranslation } from "react-i18next";

export function SellerClickPayments() {
  const { sellerId } = useParams();
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: data, isError, error } = useQuery({
    ...getClickPaymentsQuery(sellerId),
  });

  useEffect(() => {
    if (isError) {
      alert(error?.response?.data?.error?.message || error?.message);
    }
  }, [isError, error]);

  const addMutation = useMutation({
    mutationFn: (values) => addClickPayments(sellerId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["click-payments", sellerId] });
      toggle();
      formik.resetForm();
    },
    onError: (err) => {
      alert(err?.response?.data?.error?.message || err?.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteClickPayments(sellerId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["click-payments", sellerId] });
      setDeleteModal(null);
    },
    onError: (err) => {
      alert(err?.response?.data?.error?.message || err?.message);
    },
  });

  const toggle = () => setModal(!modal);

  const formik = useFormik({
    initialValues: {
      name: "",
      serviceId: "",
      secretKey: "",
      merchantUserId: "",
      commission: "",
      merchantId: "38707",
    },
    validationSchema: Yup.object({
      name: Yup.string().max(20).required(t("Required")),
      serviceId: Yup.string().required(t("Required")),
      secretKey: Yup.string().required(t("Required")),
      merchantUserId: Yup.string().required(t("Required")),
      commission: Yup.string().required("Required"),
    }),
    onSubmit: (values) => addMutation.mutate(values),
  });

  const columns = useMemo(
    () => [
      { header: t("Name"), accessorKey: "name", enableColumnFilter: false },
      {
        header: t("Service ID"),
        accessorKey: "serviceId",
        enableColumnFilter: false,
      },
      {
        header: t("Secret Key"),
        accessorKey: "secretKey",
        enableColumnFilter: false,
      },
      {
        header: t("Merchant ID"),
        accessorKey: "merchantUserId",
        enableColumnFilter: false,
      },
      {
        header: t("Commission"),
        accessorKey: "commission",
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <button
            className="btn btn-danger btn-sm"
            onClick={() => setDeleteModal(row.original.id)}
          >
            <i className="ri-delete-bin-6-fill"></i>
          </button>
        ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <div className="card-body pt-0" style={{ padding: 0 }}>
      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => deleteMutation.mutate(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
      />
      <Row className="g-4 mb-3 align-items-center">
        <div className="col-sm">
          <h5 className="card-title mb-0">{t("Click Services")}</h5>
        </div>
        <div className="col-sm-auto">
          <button className="btn btn-success" onClick={toggle}>
            <i className="ri-add-line me-1"></i> {t("create")}
          </button>
        </div>
      </Row>

      {data?.data?.data?.length > 0 ? (
        <TableContainer
          columns={columns}
          data={data?.data?.data || []}
          customPageSize={10}
        />
      ) : (
        <div className="text-center p-3 text-muted">
          <h5>{t("No Data")}</h5>
        </div>
      )}

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Add Click Service</ModalHeader>
        <Form onSubmit={formik.handleSubmit}>
          <ModalBody>
            {[
              "name",
              "serviceId",
              "secretKey",
              "merchantUserId",
              "commission",
            ].map((field) => (
              <div className="mb-3" key={field}>
                <Label className="form-label" htmlFor={field}>
                  {t(field.charAt(0).toUpperCase() + field.slice(1))}
                </Label>
                <Input
                  id={field}
                  name={field}
                  value={formik.values[field]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched[field] && !!formik.errors[field]}
                />
                {formik.touched[field] && formik.errors[field] && (
                  <FormFeedback>{formik.errors[field]}</FormFeedback>
                )}
              </div>
            ))}

            <div className="mb-3">
              <Label className="form-label" htmlFor="merchantId">
                {t("Merchant ID")}
              </Label>
              <Input id="merchantId" value="38707" disabled readOnly />
            </div>
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn-light" onClick={toggle}>
              {t("Close")}
            </button>
            <button type="submit" className="btn btn-success">
              {t("create")}
            </button>
          </ModalFooter>
        </Form>
      </Modal>

      <ToastContainer closeButton={false} limit={1} />
    </div>
  );
}
